import { ReactElement, useCallback } from 'react';
import { Alert, Button, DialogBody, DialogFooter, Spinner } from '@ff-it/ui';
import { Form, Submit, TextField, DatePickerField, useForm, useOnFieldChange } from '@ff-it/form';
import { ExpenseTypeField, ExpenseVATRateField } from 'components';
import { CompanyField } from 'modules/core/company/components';
import type { EmbeddedCompany } from 'types';
import type { ExtractedInvoice } from 'modules/invoicecapture';
import { CurrencyRateField, TotalsFields } from './Form/Fields';
import { required } from 'utilities';
import { resignAmount, signedAmount, useExtract } from 'modules/invoicecapture';
import { useNumberValidator } from './useNumberValidator';
import {
  extractCandidates,
  extractFields,
  extractForm,
  extractLoading,
  extractPdf,
  extractSplit,
  extractTitle,
} from './style.css';

function SignFollowsType(): null {
  const form = useForm();
  useOnFieldChange('type', (next) => {
    const values = form.getState().values as Record<string, any>;
    const credit = next === 'CREDIT';
    for (const name of ['vat_amount', 'total', 'alt_vat_amount', 'alt_total']) {
      const resigned = resignAmount(values[name], credit);
      if (resigned !== undefined) {
        form.change(name, resigned);
      }
    }
  });
  return null;
}

/** The seller's numbers matched more than one company — clicking a candidate fills the select. */
function PartnerCandidates({ partners }: { partners: EmbeddedCompany[] }): ReactElement {
  const form = useForm();
  return (
    <div className={extractCandidates}>
      Several providers match — pick one:
      {partners.map((company) => (
        <Button key={company.id} variant="ghost" size="sm" onClick={() => form.change('partner', company)}>
          {company.internal_name || company.title}
        </Button>
      ))}
    </div>
  );
}

/** Nothing matched — showing what we looked for tells the user whether the provider is missing or
 * the extraction misread it. */
function NoPartnerMatch({ extracted }: { extracted: ExtractedInvoice }): ReactElement | null {
  const numbers = [...new Set([extracted.seller_reg_number, extracted.seller_vat].filter(Boolean))];
  if (!extracted.seller_name && !numbers.length) {
    return null;
  }
  return (
    <>
      No provider matched <strong>{extracted.seller_name ?? 'this invoice'}</strong>
      {numbers.length > 0 && <> ({numbers.join(', ')})</>}
    </>
  );
}

interface Props {
  attachmentId: number;
  fileUrl: string;
  onSubmit: (values: Record<string, unknown>) => void;
  onDismiss: () => void;
}

/** PDF on the left, editable fields on the right; Continue carries the values to the create form. */
export function ExtractResultDialog({ attachmentId, fileUrl, onSubmit, onDismiss }: Props): ReactElement {
  const { extracted, failure, isLoading, vatRate, rateUnknown, altCurrency, initialValues, matchedPartners } =
    useExtract(attachmentId);

  // The invoice number must be provided and must be unique for partner.
  const validateNumberUnique = useNumberValidator();
  const validateNumber = useCallback(
    (value: any, allValues: any, meta: any) => required(value) ?? validateNumberUnique(value, allValues, meta),
    [validateNumberUnique],
  );

  let sellerHelp;
  if (matchedPartners.length > 1) {
    sellerHelp = <PartnerCandidates partners={matchedPartners} />;
  } else if (!matchedPartners.length && extracted) {
    sellerHelp = <NoPartnerMatch extracted={extracted} />;
  }

  const vatRateHelp = rateUnknown
    ? vatRate != null
      ? `Invoice shows ${vatRate}% VAT, which is not an available rate — pick the correct one.`
      : 'VAT rate could not be read, but the invoice carries VAT — pick the correct one.'
    : undefined;

  return (
    <Form
      className={extractForm}
      noValidate
      autoComplete="off"
      renderErrors={false}
      onSubmit={(values) => onSubmit(values)}
      initialValues={initialValues}
    >
      <DialogBody>
        <div className={extractSplit}>
          <iframe title="invoice" src={fileUrl} className={extractPdf} />
          <div className={extractFields}>
            <div className={extractTitle}>Extracted fields</div>
            {isLoading && (
              <div className={extractLoading}>
                <Spinner />
              </div>
            )}
            {failure && (
              <Alert variant="danger" mb="3">
                {failure}
              </Alert>
            )}
            {!isLoading && (
              <>
                <div className="form-row">
                  <ExpenseTypeField
                    name="type"
                    label="Type"
                    className="col-6"
                    menuPortalTarget={document.body}
                    required
                  />
                  <TextField name="number" label="Number" className="col-6" required validate={validateNumber} />
                </div>
                <div className="form-row">
                  <CompanyField
                    name="partner"
                    label="Provider"
                    className="col-12"
                    help={sellerHelp}
                    menuPortalTarget={document.body}
                    includeBranches
                    required
                    validate={required}
                  />
                </div>
                <div className="form-row">
                  <DatePickerField
                    name="checkout_date"
                    label="Invoice date"
                    className="col-6"
                    required
                    validate={required}
                  />
                  <DatePickerField
                    name="payment_date"
                    label="Payment date"
                    className="col-6"
                    required
                    validate={required}
                  />
                </div>
                <div className="form-row">
                  <CurrencyRateField className="col-6" menuPortalTarget={document.body} />
                  <ExpenseVATRateField
                    name="vat_rate"
                    label="VAT rate"
                    className="col-6"
                    help={vatRateHelp}
                    menuPortalTarget={document.body}
                    required
                    validate={required}
                  />
                </div>
                <SignFollowsType />
                <TotalsFields validate={signedAmount} columnClassName="col-6" />
                {extracted && extracted.currency !== 'EUR' && !altCurrency && (
                  <Alert variant="warning" mb="3">
                    Invoice currency {extracted.currency} — no rate on file for the invoice date. Pick the currency
                    above, or enter the amounts in EUR.
                  </Alert>
                )}
              </>
            )}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onDismiss}>
          Cancel
        </Button>
        <Submit variant="primary" className="ml-auto" disabled={isLoading}>
          Continue
        </Submit>
      </DialogFooter>
    </Form>
  );
}
